import * as routes from '../../routes'

import React, { ReactNode, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'

import LoadingAnimation from './LoadingAnimation'
import LoadingFailed from './LoadingFailed'
import { RootState } from '../../store'
import { fetchEvents } from '../../store/events/events'
import { fetchInstitution } from '../../store/institution/institution'
import { fetchProgram } from '../../store/program/program'
import { resetAll } from '../success/SuccessStage'
import { setData } from '../../store/misc/misc'
import { useLocation, useNavigate } from 'react-router-dom'

const InitialData = (props: {
  baseUrl: string
  programId: number
  event_id: string
  participant_token: string
  children?: any
  location: any
}) => {
  const { baseUrl, programId, event_id, participant_token } = props
  const dispatch = useAppDispatch()

  const { loaded: programLoaded, error: programFailed } = useAppSelector(
    (state: RootState) => state.program,
  )
  const { loaded: eventsLoaded, error: eventsFailed } = useAppSelector(
    (state: RootState) => state.events,
  )

  const {
    data: { theme },
  } = useAppSelector((state: RootState) => state.institution)

  //create components using React.lazy
  const CeruleanTheme = React.lazy(() => import('../themes/Cerulean/cerulean'))
  const CosmoTheme = React.lazy(() => import('../themes/Cosmo/cosmo'))
  const FlatlyTheme = React.lazy(() => import('../themes/Flatly/flatly'))
  const JournalTheme = React.lazy(() => import('../themes/Journal/journal'))
  const SimplexTheme = React.lazy(() => import('../themes/Simplex/simplex'))
  const SpacelabTheme = React.lazy(() => import('../themes/Spacelab/spacelab'))
  const UnitedTheme = React.lazy(() => import('../themes/United/united'))
  const YetiTheme = React.lazy(() => import('../themes/Yeti/yeti'))
  const RedbaronTheme = React.lazy(() => import('../themes/Redbaron/redbaron'))
  const ReadableTheme = React.lazy(() => import('../themes/Readable/readable'))

  const ThemeSelector = ({ children }: { children: ReactNode }) => {
    return (
      <>
        <React.Suspense fallback={<></>}>
          {theme === 'cerulean' && <CeruleanTheme />}
          {theme === 'cosmo' && <CosmoTheme />}
          {theme === 'flatly' && <FlatlyTheme />}
          {theme === 'journal' && <JournalTheme />}
          {theme === 'simplex' && <SimplexTheme />}
          {theme === 'spacelab' && <SpacelabTheme />}
          {theme === 'united' && <UnitedTheme />}
          {theme === 'yeti' && <YetiTheme />}
          {theme === 'readable' && <ReadableTheme />}
          {theme === 'redbaron' && <RedbaronTheme />}
        </React.Suspense>
        {children}
      </>
    )
  }

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    dispatch(setData({ baseUrl: baseUrl, programId: programId }))
    dispatch(fetchInstitution())
    dispatch(fetchProgram())
    resetAll(dispatch)
    if (event_id) {
      navigate(routes.EVENTS.path + '/' + event_id)
    } else {
      navigate(routes.EVENTS.path + location.hash)
    }
  }, [dispatch, baseUrl, programId])

  useEffect(() => {
    if (programLoaded) {
      dispatch(fetchEvents(participant_token, navigate))
    }
    // exclude navigate here, otherwise fetch will be triggered on each navigation!
  }, [dispatch, programLoaded, programId, participant_token])

  if (programLoaded && eventsLoaded) {
    return (
      <>
        <ThemeSelector>{props.children}</ThemeSelector>
      </>
    )
  } else if (programFailed) {
    return <LoadingFailed text='Programm konnte nicht geladen werden.' />
  } else if (eventsFailed) {
    return (
      <LoadingFailed text='Veranstaltungen konnten nicht geladen werden.' />
    )
  } else {
    return <LoadingAnimation text='Daten werden geladen...' />
  }
}

export default InitialData
